import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Lock } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About", id: "about" },
  { to: "/solutions", label: "Solutions", id: "solutions" },
  { to: "/industries", label: "Industries", id: "industries" },
  { to: "/projects", label: "Projects", id: "projects" },
  { to: "/technology", label: "Technology", id: "technology" },
  { to: "/careers", label: "Careers", id: "careers" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FFFFFF]/80 border-b border-slate-200/80" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-16 sm:h-[72px]">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-brand">
            <img src="/logo.png" alt="SATI Global Systems logo" className="h-11 w-auto object-contain" data-testid="nav-logo" />
            <span className="leading-none hidden sm:block">
              <span className="block font-heading font-bold tracking-wide text-[15px] text-slate-900">SATI GLOBAL SYSTEMS</span>
              <span className="block font-mono text-[9px] tracking-[0.35em] text-slate-500 mt-0.5">LEADING INNOVATION · EST. 2014</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="nav-links">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm rounded-md transition-colors ${
                    isActive ? "text-blue-600" : "text-slate-600 hover:text-slate-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link to="/admin/login" data-testid="nav-link-admin"
              className="p-2 text-slate-600 hover:text-blue-600 transition-colors" title="Admin Portal">
              <Lock size={15} />
            </Link>
            <Link to="/contact" data-testid="nav-cta-experts"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-[#FFFFFF] text-sm font-semibold hover:bg-blue-700 transition-colors">
              Talk to Our Experts <ArrowUpRight size={15} />
            </Link>
          </div>

          <button className="lg:hidden p-2 text-slate-700" onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle" aria-label="Toggle menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden border-t border-slate-200/80 bg-[#FFFFFF]"
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink key={l.id} to={l.to} onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className={({ isActive }) =>
                    `px-3 py-3 text-sm rounded-md ${isActive ? "text-blue-600 bg-blue-50/40" : "text-slate-700"}`
                  }>
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact" onClick={() => setOpen(false)} data-testid="nav-mobile-cta"
                className="mt-2 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full bg-blue-600 text-[#FFFFFF] text-sm font-semibold">
                Talk to Our Experts <ArrowUpRight size={15} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
